import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import ListItem from "@mui/material/ListItem";
import List from "@mui/material/List";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import DialogActions from "@mui/material/DialogActions";
import { useNavigate } from "react-router";
import { useMediaQuery } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import TableHeader from "components/Table/TableHeader";
import TableElement from "components/Table/TableElement";
import CircularIndeterminateModal from "../../utils/Loading";
import { useDispatch, useSelector } from "react-redux";
import { setLoadingPanel } from "../../redux/features/loading";
import { GetHsn, addEditHsn } from "apis/HsnApi";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { ActionButton, SearchInput, TableCard, FormCard } from "utils/ui";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function HSN() {
  const navigate = useNavigate();
  const [hsnList, setHsnList] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loadingReducer.isLoading);
  const [isEdit, setIsEdit] = useState(false);
  const [editHsnPayload, setEditHsnPayload] = useState({
    id: "",
    hsnCode: "",
    cgstPercent: 0,
    sgstPercent: 0,
    igstPercent: 0,
    utgstPercent: 0,
    cess: 0,
    dateFrom: "",
    dateTo: "",
  });
  const [addHsnPayload, setAddHsnPayload] = useState({
    hsnCode: "",
    cgstPercent: 0,
    sgstPercent: 0,
    igstPercent: 0,
    utgstPercent: 0,
    cess: 0,
    dateFrom: "",
    dateTo: "",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const openAddUpdateHSN = () => {
    setOpen(true);
    setAddHsnPayload({
      hsnCode: "",
      cgstPercent: 0,
      sgstPercent: 0,
      igstPercent: 0,
      utgstPercent: 0,
      cess: 0,
      dateFrom: "2018-01-01 00:00:00",
      dateTo: "2099-12-31 00:00:00",
    });
    setIsEdit(false);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
  };

  const getHsnList = (page = 0, query = searchQuery) => {
    dispatch(setLoadingPanel(true));
    GetHsn({ page, size: 100, query })
      .then((res) => {
        setHsnList(res.data.hsns || []);
        setTotalPages(res.data.totalPages || 1);
        dispatch(setLoadingPanel(false));
      })
      .catch((err) => {
        dispatch(setLoadingPanel(false));
        toast(err?.response?.data?.message || "Error fetching HSN list", {
          type: "error",
          position: toast.POSITION.TOP_CENTER,
          autoClose: 1000,
        });
        if (err?.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        }
      });
  };

  useEffect(() => {
    getHsnList();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      getHsnList(0, searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    getHsnList(page - 1);
    return currentPage;
  };

  const formatDateTime = (date) => {
    if (!date) return "";
    if (date.length === 10) return `${date} 00:00:00`;
    if (date.length === 19) return date;
    return date;
  };

  const saveHsn = () => {
    const payload = { ...addHsnPayload };
    delete payload.id;
    payload.dateFrom = formatDateTime(payload.dateFrom);
    payload.dateTo = formatDateTime(payload.dateTo);
    addEditHsn(payload)
      .then((res) => {
        toast("HSN Saved", {
          type: "success",
          position: toast.POSITION.TOP_CENTER,
          autoClose: 1000,
        });
        setOpen(false);
        getHsnList(0);
      })
      .catch((err) => {
        toast(err?.response?.data?.message || "Error saving HSN", {
          type: "error",
          position: toast.POSITION.TOP_CENTER,
          autoClose: 1000,
        });
      });
  };

  const onEditClick = (row) => {
    setEditHsnPayload({ ...row });
    setIsEdit(true);
    setOpen(true);
  };

  const editHsn = () => {
    const payload = { ...editHsnPayload };
    payload.dateFrom = formatDateTime(payload.dateFrom);
    payload.dateTo = formatDateTime(payload.dateTo);
    addEditHsn(payload)
      .then((res) => {
        toast("HSN Updated", {
          type: "success",
          position: toast.POSITION.TOP_CENTER,
          autoClose: 1000,
        });
        setOpen(false);
        setIsEdit(false);
        getHsnList(0);
      })
      .catch((err) => {
        toast(err?.response?.data?.message || "Error updating HSN", {
          type: "error",
          position: toast.POSITION.TOP_CENTER,
          autoClose: 1000,
        });
      });
  };

  return (
    <>
      <CircularIndeterminateModal open={isLoading} />
      <FormCard>
        <div className="flex gap-4 items-end flex-col p-4 md:flex-row md:items-center">
          <div className="w-full md:flex-1">
            <SearchInput
              placeholder="Search HSN Code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <ActionButton icon="fas fa-plus" onClick={openAddUpdateHSN}>
            Add HSN
          </ActionButton>
        </div>
      </FormCard>

      <TableCard>
        <div className="p-0">
          <div className="flex justify-center p-2 mb-2">
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                color="primary"
                onChange={handlePageChange}
                size={useMediaQuery("(max-width:768px)") ? "small" : "medium"}
              />
            </Stack>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full border-collapse" style={{ minWidth: 700 }}>
            <thead>
              <tr>
                <TableHeader value="#" />
                <TableHeader heading="HSN Code" />
                <TableHeader heading="CGST %" />
                <TableHeader heading="SGST %" />
                <TableHeader heading="IGST %" />
                <TableHeader heading="UTGST %" />
                <TableHeader heading="Cess" />
                <TableHeader heading="Date From" />
                <TableHeader heading="Date To" />
                <TableHeader heading="" />
              </tr>
            </thead>
            <tbody>
              {hsnList.map((p, index) => (
                <tr
                  key={index}
                  style={{ transition:"background 100ms" }} onMouseEnter={(e)=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
                >
                  <TableElement value={index + 1} />
                  <TableElement value={p.hsnCode} />
                  <TableElement value={p.cgstPercent} />
                  <TableElement value={p.sgstPercent} />
                  <TableElement value={p.igstPercent} />
                  <TableElement value={p.utgstPercent} />
                  <TableElement value={p.cess} />
                  <TableElement value={p.dateFrom} />
                  <TableElement value={p.dateTo} />
                  <TableElement
                    value={
                      <span
                        className="cursor-pointer text-sky-500 hover:text-sky-950 font-bold"
                        onClick={() => onEditClick(p)}
                      >
                        EDIT
                      </span>
                    }
                  />
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </TableCard>

      <div>
        <Dialog
          fullScreen
          open={open}
          onClose={handleClose}
          TransitionComponent={Transition}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {isEdit ? "Edit HSN" : "Add HSN"}
              </Typography>
            </Toolbar>
          </AppBar>
          <List sx={{ px: { xs: 1, sm: 2 } }}>
            <ListItem className="">
              <TextField
                onChange={(e) =>
                  isEdit
                    ? setEditHsnPayload({
                        ...editHsnPayload,
                        hsnCode: e.target.value,
                      })
                    : setAddHsnPayload({
                        ...addHsnPayload,
                        hsnCode: e.target.value,
                      })
                }
                value={isEdit ? editHsnPayload.hsnCode : addHsnPayload.hsnCode}
                fullWidth
                id="outlined-hsnCode"
                label="HSN Code"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </ListItem>
            <ListItem className="">
              <TextField
                type="number"
                onChange={(e) =>
                  isEdit
                    ? setEditHsnPayload({
                        ...editHsnPayload,
                        cgstPercent: e.target.value,
                      })
                    : setAddHsnPayload({
                        ...addHsnPayload,
                        cgstPercent: e.target.value,
                      })
                }
                value={isEdit ? editHsnPayload.cgstPercent : addHsnPayload.cgstPercent}
                fullWidth
                label="CGST %"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </ListItem>
            <ListItem className="">
              <TextField
                type="number"
                onChange={(e) =>
                  isEdit
                    ? setEditHsnPayload({
                        ...editHsnPayload,
                        sgstPercent: e.target.value,
                      })
                    : setAddHsnPayload({
                        ...addHsnPayload,
                        sgstPercent: e.target.value,
                      })
                }
                value={isEdit ? editHsnPayload.sgstPercent : addHsnPayload.sgstPercent}
                fullWidth
                label="SGST %"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </ListItem>
            <ListItem className="">
              <TextField
                type="number"
                onChange={(e) =>
                  isEdit
                    ? setEditHsnPayload({
                        ...editHsnPayload,
                        igstPercent: e.target.value,
                      })
                    : setAddHsnPayload({
                        ...addHsnPayload,
                        igstPercent: e.target.value,
                      })
                }
                value={isEdit ? editHsnPayload.igstPercent : addHsnPayload.igstPercent}
                fullWidth
                label="IGST %"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </ListItem>
            <ListItem className="">
              <TextField
                type="number"
                onChange={(e) =>
                  isEdit
                    ? setEditHsnPayload({
                        ...editHsnPayload,
                        utgstPercent: e.target.value,
                      })
                    : setAddHsnPayload({
                        ...addHsnPayload,
                        utgstPercent: e.target.value,
                      })
                }
                value={isEdit ? editHsnPayload.utgstPercent : addHsnPayload.utgstPercent}
                fullWidth
                label="UTGST %"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </ListItem>
            <ListItem className="">
              <TextField
                type="number"
                onChange={(e) =>
                  isEdit
                    ? setEditHsnPayload({
                        ...editHsnPayload,
                        cess: e.target.value,
                      })
                    : setAddHsnPayload({
                        ...addHsnPayload,
                        cess: e.target.value,
                      })
                }
                value={isEdit ? editHsnPayload.cess : addHsnPayload.cess}
                fullWidth
                label="Cess"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </ListItem>
            <ListItem className="" sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                type="date"
                onChange={(e) =>
                  isEdit
                    ? setEditHsnPayload({
                        ...editHsnPayload,
                        dateFrom: e.target.value,
                      })
                    : setAddHsnPayload({
                        ...addHsnPayload,
                        dateFrom: e.target.value,
                      })
                }
                value={isEdit ? (editHsnPayload.dateFrom ? editHsnPayload.dateFrom.slice(0, 10) : '') : (addHsnPayload.dateFrom ? addHsnPayload.dateFrom.slice(0, 10) : '')}
                fullWidth
                label="Date From"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2, mr: 2 }}
              />
            </ListItem>
            <ListItem className="" sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                type="date"
                onChange={(e) =>
                  isEdit
                    ? setEditHsnPayload({
                        ...editHsnPayload,
                        dateTo: e.target.value + (isEdit && editHsnPayload.dateTo && editHsnPayload.dateTo.length > 10 ? " " + editHsnPayload.dateTo.slice(11, 19) : " 00:00:00")
                      })
                    : setAddHsnPayload({
                        ...addHsnPayload,
                        dateTo: e.target.value + (addHsnPayload.dateTo && addHsnPayload.dateTo.length > 10 ? " " + addHsnPayload.dateTo.slice(11, 19) : " 00:00:00")
                      })
                }
                value={isEdit ? (editHsnPayload.dateTo ? editHsnPayload.dateTo.slice(0, 10) : '') : addHsnPayload.dateTo.slice(0, 10)}
                fullWidth
                label="Date To"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2, mr: 2 }}
              />
              <TextField
                type="text"
                label="Time"
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: 120, mb: 2 }}
                value={isEdit
                  ? (editHsnPayload.dateTo && editHsnPayload.dateTo.length > 10
                      ? editHsnPayload.dateTo.slice(11, 19)
                      : (editHsnPayload.dateTo ? "00:00:00" : "00:00:00"))
                  : (addHsnPayload.dateTo && addHsnPayload.dateTo.length > 10
                      ? addHsnPayload.dateTo.slice(11, 19)
                      : (addHsnPayload.dateTo ? "00:00:00" : "00:00:00"))
                }
                onChange={e => {
                  const time = e.target.value;
                  if (isEdit) {
                    setEditHsnPayload({
                      ...editHsnPayload,
                      dateTo: (editHsnPayload.dateTo ? editHsnPayload.dateTo.slice(0, 10) : "") + " " + time
                    });
                  } else {
                    setAddHsnPayload({
                      ...addHsnPayload,
                      dateTo: (addHsnPayload.dateTo ? addHsnPayload.dateTo.slice(0, 10) : "") + " " + time
                    });
                  }
                }}
              />
            </ListItem>
          </List>
          <DialogActions>
            {isEdit ? (
              <Button onClick={editHsn}>Save Changes</Button>
            ) : (
              <Button onClick={saveHsn}>Save</Button>
            )}
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}
